import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CreditCard, Info, X } from 'lucide-react';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Spinner } from 'components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { formatNumber } from 'helpers';
import { useAPI } from 'hooks';
import Locales from 'locales';
import EcobankOtpInput from './EcobankOtpInput';
import { createEcobankIdempotencyKey, createEcobankPaymentApi } from './ecobankPaymentApi';
import {
  ECOBANK_PRESENTATION,
  getEcobankAttempt,
  resolveEcobankPresentation,
  resolveEcobankProfilePresentation,
} from './ecobankPaymentState';

const SIX_DIGITS = /^\d{6}$/;
const L = Locales.invoice.ecobank;
const CONFIRMED_PAYMENT_QUERY_KEYS = [
  'invoice/',
  'trip/',
  'trip/stats/default/',
  'trip/stats/category/',
  'trip/stats/trip_category_paid/',
  'category/',
  'trip/stats/demographic/',
  'trip/trip_report/',
  'trip/stats/department/',
  'deposit/stats/default/',
  'invoice/stats/deposit_count/',
];
const footerClassName =
  'flex-row flex-wrap justify-end gap-2.5 border-t border-[#e8ece9] bg-[#fcfdfc] px-5 py-4 sm:space-x-0 min-[651px]:px-6';

const invalidateConfirmedPaymentQueries = (queryClient) => {
  CONFIRMED_PAYMENT_QUERY_KEYS.forEach((queryKey) => {
    queryClient.invalidateQueries({ queryKey: [queryKey] });
  });
};

const getErrorText = (error) => {
  if (typeof error === 'string') return error;
  if (typeof error?.response?.data?.detail === 'string') return error.response.data.detail;
  if (typeof error?.message === 'string') return error.message;
  return '';
};

const classifyCodeError = (error, kind) => {
  const normalized = getErrorText(error).toLocaleLowerCase('fr');
  if (kind === 'email' && /expir|périm/.test(normalized)) return 'expired';
  if (/otp|code|incorrect|invalid|expir|périm/.test(normalized)) return 'invalid';
  return 'generic';
};

const ModalHeader = ({ step, title, children }) => (
  <DialogHeader className="px-5 pb-0 pt-5 pr-14 text-left min-[651px]:px-6 min-[651px]:pt-6 min-[651px]:pr-16">
    {step && (
      <p className="text-xs font-normal text-[#78837c]">{Locales.formatString(L.message.setup_step, { step })}</p>
    )}
    <DialogTitle className="text-[19px] font-semibold leading-7 tracking-[-0.3px] text-[#20342a] min-[651px]:text-xl">
      {title}
    </DialogTitle>
    {children}
  </DialogHeader>
);

ModalHeader.propTypes = {
  step: PropTypes.number,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const TransactionSummary = ({ invoice, amount }) => (
  <div className="mt-5 flex flex-col gap-2 border-t border-[#e6ebe7] pt-4 text-sm text-[#2f3e35] sm:flex-row sm:items-center sm:justify-between sm:gap-4">
    <span>{`${invoice.code} · ${invoice.agency_name || '-'}`}</span>
    <strong className="whitespace-nowrap font-medium tabular-nums">{formatNumber(amount)} F CFA</strong>
  </div>
);

TransactionSummary.propTypes = {
  invoice: PropTypes.shape({ code: PropTypes.string, agency_name: PropTypes.string }).isRequired,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const OutcomeIcon = ({ type }) => {
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 grid size-[46px] place-items-center rounded-xl ${
        isSuccess ? 'bg-[#e8f5ed] text-[#18834b]' : 'bg-[#fbefeb] text-[#b45a43]'
      }`}
      aria-hidden="true"
    >
      {isSuccess ? <Check className="size-6" /> : <X className="size-6" />}
    </div>
  );
};

OutcomeIcon.propTypes = { type: PropTypes.oneOf(['success', 'failure']).isRequired };

const EcobankPaymentModal = ({
  invoice,
  ecobankAccount,
  isOpen,
  onOpenChange,
  initialPaymentView,
  onPaymentViewChange,
  requestState,
}) => {
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const ecobankApi = useMemo(() => createEcobankPaymentApi(api), [api]);
  const [paymentView, setPaymentView] = useState(initialPaymentView);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [qrMaterial, setQrMaterial] = useState(null);
  const qrImageUrl = qrMaterial?.imageUrl;
  const [qrError, setQrError] = useState(false);
  const [qrRequestAttempt, setQrRequestAttempt] = useState(0);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [refreshedAttemptId, setRefreshedAttemptId] = useState(null);
  const qrRequestRef = useRef(false);
  const pollCount = useRef(0);
  const onViewChangeRef = useRef(onPaymentViewChange);
  onViewChangeRef.current = onPaymentViewChange;
  const localRequestState = useRef({});
  const requests = requestState || localRequestState.current;
  if (!requests.initiationKey) requests.initiationKey = createEcobankIdempotencyKey();
  if (!requests.authorization) requests.authorization = { attemptId: null, code: null, key: null };
  const paymentPresentation = resolveEcobankPresentation(paymentView);
  const profilePresentation = resolveEcobankProfilePresentation(paymentView);
  const presentation =
    showProfileSetup && paymentPresentation === ECOBANK_PRESENTATION.PAYMENT_REJECTED
      ? profilePresentation
      : paymentPresentation;
  const amount = paymentView?.amount ?? invoice.amount;
  const attempt = getEcobankAttempt(paymentView);
  const isBusy = Boolean(busy);
  const canSubmitOtp = paymentView?.actions?.can_submit_otp === true;
  const canInitiate = paymentView?.actions?.can_initiate === true;
  const needsRetryRefresh = attempt?.error_code === 'RECORD_ALREADY_EXISTS' && refreshedAttemptId !== attempt.id;
  const canActivateProfile = paymentView?.actions?.can_activate_profile === true;
  const canResumeProfileSetup =
    paymentPresentation === ECOBANK_PRESENTATION.PAYMENT_REJECTED &&
    !canInitiate &&
    profilePresentation !== ECOBANK_PRESENTATION.UNAVAILABLE &&
    profilePresentation !== ECOBANK_PRESENTATION.PAYMENT_REVIEW;

  const applyAuthoritativeView = (nextView) => {
    const nextPaymentPresentation = resolveEcobankPresentation(nextView);
    const nextPresentation =
      showProfileSetup && nextPaymentPresentation === ECOBANK_PRESENTATION.PAYMENT_REJECTED
        ? resolveEcobankProfilePresentation(nextView)
        : nextPaymentPresentation;
    if (nextPresentation !== presentation) {
      setCode('');
      setCodeError(null);
    }
    setPaymentView(nextView);
    onPaymentViewChange(nextView);
    if (nextPaymentPresentation === ECOBANK_PRESENTATION.PAYMENT_CONFIRMED) {
      invalidateConfirmedPaymentQueries(queryClient);
    }
    return nextView;
  };

  const readAfterMutation = async (mutationView, failureKind = 'unavailable') => {
    try {
      return applyAuthoritativeView(await ecobankApi.read(invoice.id));
    } catch {
      const refreshFailureView = {
        ...mutationView,
        available: false,
        actions: {},
        unavailable_reason: null,
        ineligibility_reason: null,
      };
      if (failureKind === 'authorization') {
        const unresolvedAttempt = {
          ...(getEcobankAttempt(mutationView) || attempt || {}),
          status: 'UNKNOWN',
          error_message: null,
        };
        refreshFailureView.available = true;
        refreshFailureView.attempt = unresolvedAttempt;
        refreshFailureView.active_attempt = unresolvedAttempt;
      }
      return applyAuthoritativeView(refreshFailureView);
    }
  };

  useEffect(() => {
    if (!isOpen || presentation !== ECOBANK_PRESENTATION.PROFILE_QR || qrMaterial || qrRequestRef.current) return;

    let active = true;
    qrRequestRef.current = true;
    setQrError(false);
    ecobankApi
      .readProfileQr(invoice.id)
      .then(async (material) => {
        if (!active) return;
        if (material.imageUrl) {
          setQrMaterial(material);
        } else {
          const nextView = await ecobankApi.read(invoice.id);
          if (!active) return;
          setPaymentView(nextView);
          onViewChangeRef.current(nextView);
          if (resolveEcobankPresentation(nextView) === ECOBANK_PRESENTATION.PAYMENT_CONFIRMED) {
            invalidateConfirmedPaymentQueries(queryClient);
          }
          setQrError(true);
        }
      })
      .catch(() => {
        if (active) setQrError(true);
      })
      .finally(() => {
        qrRequestRef.current = false;
      });

    return () => {
      active = false;
    };
  }, [ecobankApi, invoice.id, isOpen, presentation, qrMaterial, qrRequestAttempt, queryClient]);

  useEffect(() => {
    const shouldPoll = presentation === ECOBANK_PRESENTATION.PAYMENT_PROCESSING;
    if (!isOpen || !shouldPoll || isBusy || actionError || pollCount.current >= 10) return undefined;
    let active = true;
    let timer;
    const poll = async () => {
      pollCount.current += 1;
      try {
        const nextView = await ecobankApi.read(invoice.id);
        if (!active) return;
        setPaymentView(nextView);
        onPaymentViewChange(nextView);
        const nextPresentation = resolveEcobankPresentation(nextView);
        if (nextPresentation === ECOBANK_PRESENTATION.PAYMENT_CONFIRMED) {
          invalidateConfirmedPaymentQueries(queryClient);
        }
        if (nextPresentation === ECOBANK_PRESENTATION.PAYMENT_PROCESSING) {
          if (pollCount.current < 10) timer = setTimeout(poll, 3000);
          else setActionError(L.message.processing_delayed);
        }
      } catch {
        pollCount.current = 10;
        if (active) setActionError(L.error.lookup);
      }
    };
    timer = setTimeout(poll, 3000);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [isOpen, presentation, isBusy, actionError, ecobankApi, invoice.id, onPaymentViewChange, queryClient]);

  const closeModal = () => {
    if (isBusy) return;
    setCode('');
    setCodeError(null);
    setShowProfileSetup(false);
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) closeModal();
  };

  const runAction = async (name, action) => {
    if (isBusy) return;
    setBusy(name);
    setActionError(null);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  const refreshStatus = () =>
    runAction('refresh', async () => {
      try {
        const nextView = applyAuthoritativeView(await ecobankApi.read(invoice.id));
        setRefreshedAttemptId(getEcobankAttempt(nextView)?.id || null);
      } catch {
        setActionError(L.error.lookup);
      }
    });

  const sendProfileOtp = () =>
    runAction('send-profile-otp', async () => {
      if (!paymentView?.actions?.can_request_profile_otp) return;
      setQrMaterial(null);
      setQrError(false);
      try {
        applyAuthoritativeView(await ecobankApi.sendProfileOtp(invoice.id));
      } catch {
        try {
          const nextView = applyAuthoritativeView(await ecobankApi.read(invoice.id));
          if (
            [ECOBANK_PRESENTATION.SETUP_REQUIRED, ECOBANK_PRESENTATION.PROFILE_UNKNOWN].includes(
              resolveEcobankProfilePresentation(nextView)
            )
          ) {
            setActionError(L.error.send);
          }
        } catch {
          applyAuthoritativeView({ available: false });
          setActionError(L.error.lookup);
        }
      }
    });

  const resendProfileOtp = () =>
    runAction('resend-profile-otp', async () => {
      if (!paymentView?.actions?.can_resend_profile_otp) return;
      try {
        applyAuthoritativeView(await ecobankApi.resendProfileOtp(invoice.id));
        setCode('');
        setCodeError(null);
      } catch {
        setActionError(L.error.resend);
      }
    });

  const validateProfileOtp = (event) => {
    event.preventDefault();
    if (!SIX_DIGITS.test(code)) return;
    runAction('validate-profile-otp', async () => {
      setCodeError(null);
      try {
        const mutationView = await ecobankApi.validateProfileOtp(invoice.id, code);
        setCode('');
        await readAfterMutation(mutationView);
      } catch (error) {
        const errorType = classifyCodeError(error, 'email');
        setCodeError(errorType);
        try {
          applyAuthoritativeView(await ecobankApi.read(invoice.id));
        } catch {
          // Keep the current authoritative view when it cannot be refreshed.
        }
      }
    });
  };

  const activateProfile = () =>
    runAction('activate-profile', async () => {
      if (!canActivateProfile) return;
      try {
        const mutationView = await ecobankApi.activateProfile(invoice.id);
        setCode('');
        setCodeError(null);
        await readAfterMutation(mutationView);
      } catch (error) {
        const activationError = getErrorText(error) || L.error.activate;
        try {
          const nextView = applyAuthoritativeView(await ecobankApi.read(invoice.id));
          const nextStatus = nextView?.customer_profile?.status;
          if (!['ACTIVATION_FAILED', 'ACTIVATED', 'ACTIVE'].includes(nextStatus)) setActionError(activationError);
        } catch {
          setActionError(activationError);
        }
      }
    });

  const retryQrRequest = () => {
    if (qrRequestRef.current) return;
    setQrError(false);
    setQrRequestAttempt((attemptNumber) => attemptNumber + 1);
  };

  const completeProfile = (event) => {
    event.preventDefault();
    if (
      !code.trim() ||
      paymentView?.customer_profile?.status !== 'ACTIVATED' ||
      !paymentView.actions?.can_complete_profile
    )
      return;
    runAction('complete-profile', async () => {
      setCodeError(null);
      try {
        const mutationView = await ecobankApi.completeProfile(invoice.id, code.trim());
        setCode('');
        await readAfterMutation(mutationView);
      } catch (error) {
        setCodeError(classifyCodeError(error, 'authenticator'));
        try {
          applyAuthoritativeView(await ecobankApi.read(invoice.id));
        } catch {
          setActionError(L.error.lookup);
        }
      }
    });
  };

  const initiatePayment = () =>
    runAction('initiate-payment', async () => {
      if (!canInitiate || needsRetryRefresh) return;
      if (attempt?.status === 'FAILED' && requests.retryAttemptId !== attempt.id) {
        requests.initiationKey = createEcobankIdempotencyKey();
        requests.retryAttemptId = attempt.id;
      }
      try {
        const mutationView = await ecobankApi.initiatePayment(invoice.id, requests.initiationKey);
        setShowProfileSetup(false);
        pollCount.current = 0;
        await readAfterMutation(mutationView);
      } catch {
        setActionError(L.error.initiate);
      }
    });

  const authorizePayment = (event) => {
    event.preventDefault();
    if (!SIX_DIGITS.test(code) || !attempt?.id || !canSubmitOtp) return;
    runAction('authorize-payment', async () => {
      setCodeError(null);
      try {
        if (requests.authorization.code !== code || requests.authorization.attemptId !== attempt.id) {
          requests.authorization = { attemptId: attempt.id, code, key: createEcobankIdempotencyKey() };
        }
        const mutationView = await ecobankApi.authorizePayment(
          invoice.id,
          attempt.id,
          code,
          requests.authorization.key
        );
        setCode('');
        await readAfterMutation(mutationView, 'authorization');
      } catch (error) {
        setCodeError(classifyCodeError(error, 'authenticator'));
        try {
          applyAuthoritativeView(await ecobankApi.read(invoice.id));
        } catch {
          applyAuthoritativeView({
            ...paymentView,
            available: false,
            actions: {},
            unavailable_reason: null,
          });
          setActionError(L.error.lookup);
        }
      }
    });
  };

  const renderSetupRetry = () => (
    <>
      <ModalHeader title={L.modal.setup_retry_title} />
      <div className="space-y-5 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6 text-sm leading-[22px] text-[#6a766f]">
        <DialogDescription className="text-sm leading-[22px] text-[#6a766f]">
          {paymentView?.customer_profile?.status === 'UNKNOWN' ? L.message.profile_unknown : L.message.setup_retry}
        </DialogDescription>
        {actionError && (
          <p className="text-destructive" role="alert">
            {actionError}
          </p>
        )}
      </div>
      <DialogFooter className={footerClassName}>
        <Button type="button" variant="modal-primary" size="modal" disabled={isBusy} onClick={sendProfileOtp}>
          {busy === 'send-profile-otp' && <Spinner className="mr-1" aria-label={L.loading.send} />}
          {busy === 'send-profile-otp' ? L.loading.send : L.action.retry}
        </Button>
      </DialogFooter>
    </>
  );

  const renderEmailCode = () => {
    const isExpired = codeError === 'expired';
    const canResend = Boolean(
      paymentView?.customer_profile?.status === 'OTP_SENT' && paymentView?.actions?.can_resend_profile_otp === true
    );
    const errorCopy =
      codeError === 'expired' ? (
        <>
          <strong>{L.error.expired_email_title}</strong>
          <br />
          {L.error.expired_email_help}
        </>
      ) : codeError === 'invalid' ? (
        <>
          <strong>{L.error.invalid_email_title}</strong>
          <br />
          {L.error.invalid_email_help}
        </>
      ) : codeError === 'generic' ? (
        L.error.generic_code
      ) : null;

    return (
      <form onSubmit={validateProfileOtp}>
        <ModalHeader step={1} title={L.modal.email_title} />
        <div className="space-y-5 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
          <DialogDescription className="text-sm leading-[22px] text-[#6a766f]">
            {L.message.setup_notice}
            <br />
            <br />
            {L.message.email_sent}
            <br />
            <strong className="mt-[7px] inline-block font-medium text-[#34483b]">
              {paymentView?.customer_profile?.token_recipient_hint || L.message.masked_unavailable}
            </strong>
          </DialogDescription>
          <EcobankOtpInput
            id="ecobank-profile-email-code"
            label={L.field.email_code}
            value={code}
            onChange={setCode}
            disabled={isBusy || isExpired}
            error={errorCopy}
            autoFocus
          />
          <p className="!mt-3 text-[13px] leading-5 text-[#738079]">{L.message.email_expiry}</p>
          {actionError && (
            <p className="text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}
        </div>
        <DialogFooter className={footerClassName}>
          {isExpired && !canResend && (
            <Button type="button" variant="modal-secondary" size="modal" disabled={isBusy} onClick={refreshStatus}>
              {L.action.refresh}
            </Button>
          )}
          {canResend && (
            <Button type="button" variant="modal-secondary" size="modal" disabled={isBusy} onClick={resendProfileOtp}>
              {busy === 'resend-profile-otp' && <Spinner className="mr-1" aria-label={L.loading.send} />}
              {busy === 'resend-profile-otp' ? L.loading.send : L.action.resend}
            </Button>
          )}
          {!isExpired && (
            <Button
              type="submit"
              variant="modal-primary"
              size="modal"
              disabled={isBusy || isExpired || !SIX_DIGITS.test(code)}
            >
              {busy === 'validate-profile-otp' && <Spinner className="mr-1" aria-label={L.loading.validate} />}
              {busy === 'validate-profile-otp' ? L.loading.validate : L.action.validate}
            </Button>
          )}
        </DialogFooter>
      </form>
    );
  };

  const renderQr = () => (
    <>
      <ModalHeader step={2} title={L.modal.qr_title} />
      <div className="space-y-4 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
        <DialogDescription className="text-sm leading-[22px] text-[#6a766f]">
          {L.message.qr_instruction}
        </DialogDescription>
        <p className="text-sm font-medium text-[#53645a]">{L.message.qr_camera_warning}</p>
        <div className="flex min-h-[232px] items-center justify-center py-1">
          {!qrMaterial && !qrError && <Spinner aria-label={L.loading.qr} />}
          {qrImageUrl && (
            <img
              src={qrImageUrl}
              width="216"
              height="216"
              className="size-[216px] max-w-full object-contain"
              alt="QR code d’association Ecobank Authenticator"
            />
          )}
          {qrError && (
            <p className="text-center text-sm text-destructive" role="alert">
              {L.error.qr}
            </p>
          )}
        </div>
        <p className="text-sm leading-[22px] text-[#6a766f]">{L.message.qr_followup}</p>
        {actionError && (
          <p className="text-sm text-destructive" role="alert">
            {actionError}
          </p>
        )}
      </div>
      <DialogFooter className={footerClassName}>
        {qrError && (
          <Button type="button" variant="modal-secondary" size="modal" onClick={retryQrRequest}>
            {L.action.retry}
          </Button>
        )}
        <Button
          type="button"
          variant="modal-primary"
          size="modal"
          disabled={isBusy || !qrImageUrl || !canActivateProfile}
          onClick={activateProfile}
        >
          {isBusy ? L.loading.checking : L.action.activate_profile}
        </Button>
      </DialogFooter>
    </>
  );

  const renderProfileActivation = () => {
    const activationFailed = paymentView?.customer_profile?.status === 'ACTIVATION_FAILED';
    const activationError =
      actionError || (activationFailed && (paymentView.unavailable_reason || L.message.activation_failed));
    return (
      <>
        <ModalHeader step={2} title={L.modal.activation_title} />
        <div className="space-y-4 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
          {activationError ? (
            <DialogDescription asChild className="text-sm leading-[22px] text-destructive">
              <p role="alert">{activationError}</p>
            </DialogDescription>
          ) : (
            <DialogDescription className="text-sm leading-[22px] text-[#6a766f]">
              {L.message.existing_profile}
            </DialogDescription>
          )}
        </div>
        <DialogFooter className={footerClassName}>
          <Button
            type="button"
            variant="modal-primary"
            size="modal"
            disabled={isBusy || !canActivateProfile}
            onClick={activateProfile}
          >
            {isBusy ? L.loading.checking : activationFailed ? L.action.retry_activation : L.action.activate_profile}
          </Button>
        </DialogFooter>
      </>
    );
  };

  const renderProfileCompletion = () => (
    <form onSubmit={completeProfile}>
      <ModalHeader step={3} title={L.modal.completion_title} />
      <div className="space-y-5 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
        <DialogDescription>{L.message.profile_token}</DialogDescription>
        <Label htmlFor="ecobank-profile-token">{L.field.profile_token}</Label>
        <Input
          id="ecobank-profile-token"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={isBusy}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={Boolean(codeError)}
          aria-describedby={codeError ? 'ecobank-token-error' : undefined}
        />
        {codeError && (
          <p id="ecobank-token-error" className="text-sm text-destructive" role="alert">
            {L.error.generic_code}
          </p>
        )}
        {actionError && (
          <p className="text-sm text-destructive" role="alert">
            {actionError}
          </p>
        )}
      </div>
      <DialogFooter className={footerClassName}>
        <Button type="submit" variant="modal-primary" size="modal" disabled={isBusy || !code.trim()}>
          {busy === 'complete-profile' && <Spinner className="mr-1" aria-label={L.loading.complete} />}
          {busy === 'complete-profile' ? L.loading.complete : L.action.complete_setup}
        </Button>
      </DialogFooter>
    </form>
  );

  const renderReview = () => {
    const pilgrimCount = invoice.trips?.length || invoice.deposit_count || 0;
    return (
      <>
        <ModalHeader title={L.modal.review_title}>
          <DialogDescription className="text-sm text-[#778279]">{L.modal.review_description}</DialogDescription>
        </ModalHeader>
        <div className="space-y-5 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <span className="block text-sm text-[#6c7970]">{L.field.invoice}</span>
              <strong className="text-sm font-medium text-[#2b3e32]">{invoice.code}</strong>
            </div>
            <div>
              <span className="block text-sm text-[#6c7970]">{L.field.agency}</span>
              <strong className="text-sm font-medium text-[#2b3e32]">{invoice.agency_name || '-'}</strong>
            </div>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-sm text-[#6c7970]">{L.field.pilgrims}</span>
            <strong className="text-sm font-medium text-[#2b3e32]">{pilgrimCount}</strong>
          </div>
          <div className="rounded-lg border border-[#e1e7e3] bg-[#f7f9f8] px-5 py-4">
            <span className="block text-sm text-[#6c7970]">{L.field.amount_due}</span>
            <strong className="mt-1 block text-[28px] font-semibold leading-9 tracking-[-0.02em] text-[#22382b]">
              {formatNumber(amount)} <span className="text-base font-medium text-[#56665b]">F CFA</span>
            </strong>
          </div>
          <div className="flex items-center gap-3 border-b border-[#e5e9e6] pb-5">
            <span className="grid size-10 shrink-0 place-items-center rounded-lg border border-[#e0e7e2] bg-[#fafbfa] text-[#758178]">
              <CreditCard className="size-5" aria-hidden="true" />
            </span>
            <div>
              <span className="block text-sm text-[#6c7970]">{L.field.account_due}</span>
              <strong className="font-medium tracking-[0.12em] text-[#3a4e41]">{ecobankAccount || '-'}</strong>
            </div>
          </div>
          <p className="text-sm leading-[22px] text-[#6a766f]">{L.message.review}</p>
          {busy === 'initiate-payment' && (
            <div
              className="flex items-center gap-2 rounded-lg bg-[#f6f8fa] px-4 py-3 text-sm text-[#667582]"
              role="status"
            >
              <Spinner aria-label={L.loading.initiate} />
              {L.loading.initiate}
            </div>
          )}
          {actionError && (
            <p className="text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}
        </div>
        <DialogFooter className={footerClassName}>
          <Button type="button" variant="modal-secondary" size="modal" disabled={isBusy} onClick={closeModal}>
            {L.action.cancel}
          </Button>
          <Button type="button" variant="modal-primary" size="modal" disabled={isBusy} onClick={initiatePayment}>
            {busy === 'initiate-payment' && <Spinner className="mr-1" aria-label={L.loading.initiate} />}
            {busy === 'initiate-payment' ? L.loading.initiate : L.action.next}
          </Button>
        </DialogFooter>
      </>
    );
  };

  const renderPaymentAuthorization = () => (
    <form onSubmit={authorizePayment}>
      <ModalHeader title={L.modal.authorization_title}>
        <DialogDescription className="text-sm text-[#778279]">
          {L.field.invoice} {invoice.code}
        </DialogDescription>
      </ModalHeader>
      <div className="space-y-5 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
        <div className="flex flex-col gap-3 rounded-lg border border-[#e0e7e2] bg-[#f8faf9] px-4 py-3.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div>
            <span className="block text-sm text-[#6c7970]">{L.field.amount}</span>
            <strong className="text-lg font-semibold tabular-nums text-[#22382b]">{formatNumber(amount)} F CFA</strong>
          </div>
          <div className="sm:text-right">
            <span className="block text-sm text-[#6c7970]">{L.field.account}</span>
            <strong className="font-medium tracking-[0.12em] text-[#3a4e41]">{ecobankAccount || '-'}</strong>
          </div>
        </div>
        <DialogDescription className="text-sm leading-[22px] text-[#6a766f]">
          {L.message.payment_authenticator}
        </DialogDescription>
        <EcobankOtpInput
          id="ecobank-payment-authenticator-code"
          label={L.field.payment_code}
          value={code}
          onChange={setCode}
          disabled={isBusy || !canSubmitOtp}
          error={
            attempt?.error_message ||
            (codeError === 'invalid' ? (
              <>
                <strong>{L.error.invalid_authenticator_title}</strong>
                <br />
                {L.error.invalid_payment_help}
              </>
            ) : codeError === 'generic' ? (
              L.error.generic_code
            ) : null)
          }
          autoFocus
        />
        {actionError && (
          <p className="text-sm text-destructive" role="alert">
            {actionError}
          </p>
        )}
        {busy === 'authorize-payment' && (
          <div
            className="flex items-center gap-2 rounded-lg bg-[#f6f8fa] px-4 py-3 text-sm text-[#667582]"
            role="status"
          >
            <Spinner aria-label={L.loading.process} />
            {L.loading.process}
          </div>
        )}
      </div>
      <DialogFooter className={footerClassName}>
        {!canSubmitOtp && (
          <Button type="button" variant="modal-secondary" size="modal" disabled={isBusy} onClick={refreshStatus}>
            {busy === 'refresh' ? L.loading.checking : L.action.refresh}
          </Button>
        )}
        <Button
          type="submit"
          variant="modal-primary"
          size="modal"
          disabled={isBusy || !SIX_DIGITS.test(code) || !canSubmitOtp}
        >
          {busy === 'authorize-payment' && <Spinner className="mr-1" aria-label={L.loading.process} />}
          {busy === 'authorize-payment' ? L.loading.process : L.action.validate_payment}
        </Button>
      </DialogFooter>
    </form>
  );

  const renderProcessing = () => (
    <>
      <ModalHeader title={L.modal.processing_title}>
        <DialogDescription className="text-sm text-[#778279]">
          {L.field.invoice} {invoice.code}
        </DialogDescription>
      </ModalHeader>
      <div className="px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
        <div className="flex items-center gap-2 rounded-lg bg-[#f6f8fa] px-4 py-3 text-sm text-[#667582]" role="status">
          <Spinner aria-label={L.loading.process} />
          {L.loading.process}
        </div>
        {actionError && (
          <p className="mt-3 text-sm text-destructive" role="alert">
            {actionError}
          </p>
        )}
      </div>
      {(actionError || pollCount.current >= 10) && (
        <DialogFooter className={footerClassName}>
          <Button type="button" variant="modal-primary" size="modal" disabled={isBusy} onClick={refreshStatus}>
            {busy === 'refresh' && <Spinner className="mr-1" aria-label={L.loading.checking} />}
            {busy === 'refresh' ? L.loading.checking : L.action.refresh}
          </Button>
        </DialogFooter>
      )}
    </>
  );

  const renderOutcome = () => {
    const isConfirmed = presentation === ECOBANK_PRESENTATION.PAYMENT_CONFIRMED;
    const isRejected = presentation === ECOBANK_PRESENTATION.PAYMENT_REJECTED;
    const title = isConfirmed
      ? L.modal.confirmed_title
      : isRejected
      ? L.modal.rejected_title
      : L.modal.unresolved_title;
    const description = isConfirmed
      ? L.message.confirmed
      : isRejected
      ? attempt?.error_message || L.message.rejected
      : L.message.unresolved;

    return (
      <>
        <ModalHeader title={title}>
          <DialogDescription className="text-sm text-[#778279]">
            {L.field.invoice} {invoice.code}
          </DialogDescription>
        </ModalHeader>
        <div className="px-5 py-5 min-[651px]:px-6 min-[651px]:py-6">
          {isConfirmed || isRejected ? (
            <OutcomeIcon type={isConfirmed ? 'success' : 'failure'} />
          ) : (
            <div
              className="mb-4 grid size-[46px] place-items-center rounded-xl bg-[#eef3f7] text-[#566f82]"
              aria-hidden="true"
            >
              <Info className="size-6" />
            </div>
          )}
          <p
            className={`text-sm leading-[22px] ${isRejected ? 'text-destructive' : 'text-[#6a766f]'}`}
            role={isRejected ? 'alert' : undefined}
          >
            {description}
          </p>
          {isRejected && attempt?.error_code === 'INSUFFICIENT_FUNDS' && (
            <p className="mt-3 text-sm text-[#6a766f]">{L.message.insufficient_funds}</p>
          )}
          {!isConfirmed && (
            <div className="mt-4 rounded-lg bg-[#f6f8fa] px-4 py-3 text-sm leading-[22px] text-[#667582]">
              {isRejected ? L.message.invoice_pending : L.message.unresolved_lock}
            </div>
          )}
          {!isConfirmed && !isRejected && <p className="mt-3 text-sm text-[#6a766f]">{L.message.invoice_pending}</p>}
          {actionError && actionError !== description && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {actionError}
            </p>
          )}
          <TransactionSummary invoice={invoice} amount={amount} />
        </div>
        {isRejected && (
          <DialogFooter className={footerClassName}>
            {(needsRetryRefresh || (!canInitiate && !canResumeProfileSetup)) && (
              <Button type="button" variant="modal-secondary" size="modal" disabled={isBusy} onClick={refreshStatus}>
                {busy === 'refresh' ? L.loading.checking : L.action.refresh}
              </Button>
            )}
            {!needsRetryRefresh && (canInitiate || canResumeProfileSetup) && (
              <Button
                type="button"
                variant="modal-primary"
                size="modal"
                disabled={isBusy}
                onClick={canResumeProfileSetup ? () => setShowProfileSetup(true) : initiatePayment}
              >
                {canResumeProfileSetup
                  ? L.action.continue_setup
                  : busy === 'initiate-payment'
                  ? L.loading.initiate
                  : L.action.restart_payment}
              </Button>
            )}
          </DialogFooter>
        )}
        {!isConfirmed && !isRejected && (
          <DialogFooter className={footerClassName}>
            <Button type="button" variant="modal-primary" size="modal" disabled={isBusy} onClick={refreshStatus}>
              {busy === 'refresh' && <Spinner className="mr-1" aria-label={L.loading.checking} />}
              {busy === 'refresh' ? L.loading.checking : L.action.refresh}
            </Button>
          </DialogFooter>
        )}
      </>
    );
  };

  const renderUnavailable = () => {
    const unavailableReason = paymentView?.unavailable_reason || paymentView?.ineligibility_reason;
    return (
      <>
        <ModalHeader title={L.modal.unavailable_title}>
          <DialogDescription className="sr-only">Le paiement Ecobank ne peut pas continuer.</DialogDescription>
        </ModalHeader>
        <div className="space-y-4 px-5 py-5 min-[651px]:px-6 min-[651px]:py-6 text-sm leading-[22px] text-[#6a766f]">
          <p>{L.message.unavailable}</p>
          {unavailableReason && (
            <p className="text-destructive" role="alert">
              {unavailableReason}
            </p>
          )}
          {actionError && actionError !== unavailableReason && (
            <p className="text-destructive" role="alert">
              {actionError}
            </p>
          )}
        </div>
        <DialogFooter className={footerClassName}>
          <Button type="button" variant="modal-primary" size="modal" disabled={isBusy} onClick={refreshStatus}>
            {busy === 'refresh' && <Spinner className="mr-1" aria-label={L.loading.checking} />}
            {busy === 'refresh' ? L.loading.checking : L.action.refresh}
          </Button>
        </DialogFooter>
      </>
    );
  };

  const content = {
    [ECOBANK_PRESENTATION.SETUP_REQUIRED]: renderSetupRetry,
    [ECOBANK_PRESENTATION.PROFILE_UNKNOWN]: renderSetupRetry,
    [ECOBANK_PRESENTATION.PROFILE_EMAIL_CODE]: renderEmailCode,
    [ECOBANK_PRESENTATION.PROFILE_QR]: renderQr,
    [ECOBANK_PRESENTATION.PROFILE_ACTIVATION]: renderProfileActivation,
    [ECOBANK_PRESENTATION.PROFILE_COMPLETION]: renderProfileCompletion,
    [ECOBANK_PRESENTATION.PAYMENT_REVIEW]: renderReview,
    [ECOBANK_PRESENTATION.PAYMENT_PROCESSING]: renderProcessing,
    [ECOBANK_PRESENTATION.PAYMENT_AUTHORIZATION]: renderPaymentAuthorization,
    [ECOBANK_PRESENTATION.PAYMENT_CONFIRMED]: renderOutcome,
    [ECOBANK_PRESENTATION.PAYMENT_REJECTED]: renderOutcome,
    [ECOBANK_PRESENTATION.PAYMENT_UNRESOLVED]: renderOutcome,
    [ECOBANK_PRESENTATION.UNAVAILABLE]: renderUnavailable,
  }[presentation];

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        closeDisabled={isBusy}
        overlayClassName="bg-[#162420]/35"
        closeClassName="right-5 top-5 grid size-7 place-items-center rounded-md p-1 text-[#6f7c74] opacity-100 hover:bg-[#f1f4f2] min-[651px]:right-6 min-[651px]:top-6"
        className="max-h-[calc(100dvh-3rem)] w-[calc(100%-2rem)] max-w-[512px] gap-0 overflow-y-auto rounded-[14px] sm:rounded-[14px] border-[#e0e6e2] bg-white p-0 font-['Inter',Arial,sans-serif] text-sm leading-[1.5] text-[#27342e] shadow-[0_18px_65px_#18362b26] outline-none"
      >
        {content()}
      </DialogContent>
    </Dialog>
  );
};

EcobankPaymentModal.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    code: PropTypes.string.isRequired,
    agency_name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deposit_count: PropTypes.number,
    trips: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  ecobankAccount: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialPaymentView: PropTypes.shape({
    available: PropTypes.bool,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unavailable_reason: PropTypes.string,
    ineligibility_reason: PropTypes.string,
    actions: PropTypes.objectOf(PropTypes.bool),
    customer_profile: PropTypes.shape({
      status: PropTypes.string,
      token_recipient_hint: PropTypes.string,
    }),
    attempt: PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
      error_code: PropTypes.string,
      error_message: PropTypes.string,
      cooldown_until: PropTypes.string,
    }),
    active_attempt: PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
      error_message: PropTypes.string,
      error_code: PropTypes.string,
      cooldown_until: PropTypes.string,
    }),
  }).isRequired,
  onPaymentViewChange: PropTypes.func.isRequired,
  requestState: PropTypes.shape({
    initiationKey: PropTypes.string,
    retryAttemptId: PropTypes.string,
    authorization: PropTypes.object,
  }),
};

export default EcobankPaymentModal;
